import "../compliance/compliance.css";
import "../appraisals/appraisals.css";
import "./surveys.css";
import { useEffect, useMemo, useState } from "react";
import { DatePicker, Input, InputNumber, Modal, Select, Switch } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import debounce from "lodash/debounce";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getSurveyAudienceOptionsQuery, getSurveyOurSideOptionsQuery, getSurveySettingsQuery, previewSurveyAudienceQuery } from "@src/util/query/survey.query";
import { sendSurveyMutation } from "@src/util/mutation/survey.mutation";
import { describeRepeat } from "../appraisals/appraisalHelpers";

const AUDIENCES = [
    { key: "employees", title: "Employees", text: "In the app, and by email." },
    { key: "customers", title: "Customers", text: "By email - no login needed." },
    { key: "suppliers", title: "Suppliers", text: "By email - no login needed." },
];
const MODES = {
    employees: [{ value: "all", label: "All employees" }, { value: "branch", label: "Employees in a branch" }, { value: "site", label: "Employees at a site" }, { value: "selected", label: "Chosen people" }],
    customers: [{ value: "all", label: "All customers" }, { value: "selected", label: "Chosen customers" }],
    suppliers: [{ value: "all", label: "All suppliers" }, { value: "selected", label: "Chosen suppliers" }],
};
const HOW_OFTEN = [
    { key: "once", title: "Just once" },
    { key: 3, title: "Every 3 months" },
    { key: 6, title: "Every 6 months" },
    { key: 12, title: "Every year" },
    { key: "custom", title: "Custom" },
];

/**
 * Send a survey: which form, who to (employees - all, a branch, a site or chosen people -
 * customers or suppliers), anonymous or not (employees only), when it goes out and closes, and
 * once or again every N months from the day it first goes out. A two-way form goes to
 * customers or suppliers only, and asks who answers our side (everyone with a role, or one
 * person). `preset` sends it to one record (from an employee's / customer's / supplier's
 * Surveys tab).
 */
export const SendSurveyModal = ({ visible, preset = null, onClose, onSent = () => {} }) => {
    const dispatch = useDispatch();
    const [templateId, setTemplateId] = useState(null);
    const [title, setTitle] = useState("");
    const [type, setType] = useState("employees");
    const [mode, setMode] = useState("all");
    const [ids, setIds] = useState([]);
    const [anonymous, setAnonymous] = useState(false);
    const [opensOn, setOpensOn] = useState(dayjs().startOf("day"));
    const [closesOn, setClosesOn] = useState(dayjs().add(14, "day"));
    const [howOften, setHowOften] = useState("once");
    const [customMonths, setCustomMonths] = useState(3);
    const [ourBy, setOurBy] = useState("role");
    const [ourRole, setOurRole] = useState(null);
    const [ourId, setOurId] = useState(null);

    const { data: settingsData } = useQuery(getSurveySettingsQuery, { skip: !visible, fetchPolicy: "network-only" });
    const { data: optionsData } = useQuery(getSurveyAudienceOptionsQuery, { skip: !visible || !!preset, fetchPolicy: "network-only" });
    const [preview, { data: previewData, loading: previewing }] = useLazyQuery(previewSurveyAudienceQuery, { fetchPolicy: "network-only" });
    const [send, { loading }] = useMutation(sendSurveyMutation);
    // an employee's record can't be sent a two-way survey (their two-way review is an appraisal)
    const templates = (settingsData?.getSurveySettings?.response?.templates || []).filter((t) => !t.isArchived && !(t.twoWay && preset?.type === "employees"));
    const options = optionsData?.getSurveyAudienceOptions?.response || {};
    const twoWay = templates.find((t) => t._id === templateId)?.twoWay === true;
    const { data: ourData } = useQuery(getSurveyOurSideOptionsQuery, { variables: { ourRole: ourBy === "role" ? ourRole : null }, skip: !visible || !twoWay, fetchPolicy: "network-only" });
    const ourOptions = ourData?.getSurveyOurSideOptions?.response;
    const ourSuggested = (ourOptions?.people || []).filter((p) => p.suggested);

    useEffect(() => {
        if (!visible) return;
        setTemplateId(templates[0]?._id || null);
        setTitle("");
        setType(preset?.type || "employees");
        setMode(preset ? "selected" : "all");
        setIds(preset ? [preset.id] : []);
        setAnonymous(false);
        setOpensOn(dayjs().startOf("day"));
        setClosesOn(dayjs().add(14, "day"));
        setHowOften("once");
        setCustomMonths(3);
        setOurBy("role");
        setOurId(null);
    }, [visible, preset?.id]);

    useEffect(() => {
        if (!templateId && templates.length) setTemplateId(templates[0]._id);
    }, [templates.length]);

    // two-way: customers or suppliers only, and someone to answer our side
    useEffect(() => {
        if (twoWay && type === "employees" && !preset) changeType("customers");
    }, [twoWay]);
    useEffect(() => {
        if (!ourRole && ourOptions?.roles?.length) setOurRole(ourOptions.roles[0].value);
    }, [ourOptions?.roles?.length]);

    // who it goes to, re-checked as the choice changes
    const runPreview = useMemo(() => debounce((audience) => preview({ variables: { audience } }), 300), []);
    useEffect(() => {
        if (!visible) return;
        if (mode !== "all" && !ids.length) return;
        runPreview({ type, mode, ids });
    }, [visible, type, mode, ids.join(",")]);
    const audience = previewData?.previewSurveyAudience?.response;

    const pickFrom = type === "employees" ? (mode === "branch" ? options.branches : mode === "site" ? options.sites : options.employees) : type === "customers" ? options.customers : options.suppliers;
    const pickLabel = type === "employees" ? (mode === "branch" ? "Branches" : mode === "site" ? "Sites" : "People") : type === "customers" ? "Customers" : "Suppliers";
    const repeatMonths = howOften === "once" ? null : howOften === "custom" ? customMonths : howOften;
    const opensToday = opensOn && opensOn.isSame(dayjs(), "day");

    const changeType = (next) => {
        setType(next);
        setMode("all");
        setIds([]);
        if (next !== "employees") setAnonymous(false);
    };
    const changeOpensOn = (value) => {
        if (!value) return;
        const days = closesOn && opensOn ? closesOn.startOf("day").diff(opensOn.startOf("day"), "day") : 14;
        setOpensOn(value.startOf("day"));
        setClosesOn(value.add(Math.max(days, 1), "day"));
    };

    const missing = [];
    if (!templateId) missing.push("a survey template");
    if (mode !== "all" && !ids.length) missing.push(pickLabel.toLowerCase());
    if (howOften === "custom" && !(customMonths >= 1)) missing.push("how many months");
    if (twoWay && ourBy === "person" && !ourId) missing.push("who answers our side");

    const when = () => {
        if (!opensOn || !closesOn) return "";
        const first = opensToday ? `Goes out now and closes on ${closesOn.format("DD/MM/YYYY")}.` : `Goes out on ${opensOn.format("DD/MM/YYYY")} and closes on ${closesOn.format("DD/MM/YYYY")} - nothing is sent until then.`;
        if (!repeatMonths) return first;
        return `${first} Then ${describeRepeat(repeatMonths).toLowerCase()} - next goes out ${opensOn.add(repeatMonths, "month").format("DD/MM/YYYY")}, to whoever is in the group then.`;
    };

    const save = async () => {
        try {
            const res = await send({
                variables: {
                    input: {
                        templateId, title: title.trim() || undefined,
                        audience: { type, mode, ids: mode === "all" ? [] : ids },
                        anonymous: type === "employees" ? anonymous : false,
                        opensOn: opensOn.format("YYYY-MM-DD"), closesOn: closesOn.format("YYYY-MM-DD"),
                        ...(repeatMonths ? { repeatEveryMonths: repeatMonths } : {}),
                        ...(twoWay ? (ourBy === "role" ? { ourRole } : { ourEmployeeId: ourId }) : {}),
                    },
                },
            });
            const r = res?.data?.sendSurvey?.response;
            const repeats = r?.everyMonths ? ` It repeats ${describeRepeat(r.everyMonths).toLowerCase()}.` : "";
            const message = r?._id
                ? `Survey sent to ${r.invited} ${r.invited === 1 ? "person" : "people"}.${r.withoutEmail ? ` ${r.withoutEmail} ${r.withoutEmail === 1 ? "has" : "have"} no email - copy their link from the results.` : ""}${repeats}`
                : `Survey scheduled - it goes out on ${dayjs(r?.opensOn).format("DD/MM/YYYY")}.${repeats}`;
            dispatch(setNotification({ error: false, notificationState: true, message }));
            onSent(r);
            onClose();
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err.message }));
        }
    };

    return (
        <Modal
            open={visible}
            width={680}
            closable={false}
            onCancel={onClose}
            footer={
                <div className="drawer-footer" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button className={loading || missing.length ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={loading || missing.length > 0} onClick={save}>
                        {loading ? <Spinner /> : opensToday ? "Send survey" : "Schedule survey"}
                    </button>
                    <button className={loading ? "disabled-btn drawer-outlined-btn" : "drawer-outlined-btn"} disabled={loading} onClick={onClose}>Cancel</button>
                    {missing.length > 0 && <span className="cmp-hint">Choose {missing.join(", ")}</span>}
                </div>
            }
        >
            <div className="modal-header-title">
                <span style={{ letterSpacing: "0.2px" }}>{preset ? `Send a survey to ${preset.name}` : "Send a survey"}</span>
                <span onClick={onClose}><FontAwesomeIcon className="close" icon={faClose} /></span>
            </div>
            <div className="modal-body apr-form" style={{ margin: "16px 0 0" }}>
                <div style={{ display: "flex", gap: 12 }}>
                    <div className="cmp-field" style={{ flex: 1 }}>
                        <label>Survey</label>
                        <Select className="custom-select" value={templateId} onChange={setTemplateId} placeholder={templates.length ? "Choose" : "Create a survey template in Settings > Appraisals & Surveys"}
                            options={templates.map((t) => ({ value: t._id, label: t.twoWay ? `${t.name} (two-way)` : t.name }))} />
                    </div>
                    <div className="cmp-field" style={{ flex: 1 }}>
                        <label>Title <span className="cmp-muted">(optional)</span></label>
                        <Input className="generic-input-control" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={templates.find((t) => t._id === templateId)?.name || "Uses the survey's name"} />
                    </div>
                </div>

                {!preset && (
                    <>
                        <div className="cmp-field">
                            <label>Who is it for</label>
                            <div className="cmp-choice">
                                {AUDIENCES.filter((a) => !twoWay || a.key !== "employees").map((a) => (
                                    <button key={a.key} type="button" className={`cmp-choice-card${type === a.key ? " selected" : ""}`} onClick={() => changeType(a.key)}>
                                        <div className="cmp-choice-card-title">{a.title}</div>
                                        <div className="cmp-choice-card-text">{a.text}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 12 }}>
                            <div className="cmp-field" style={{ width: 220 }}>
                                <Select className="custom-select" value={mode} onChange={(m) => { setMode(m); setIds([]); }} options={MODES[type]} />
                            </div>
                            {mode !== "all" && (
                                <div className="cmp-field" style={{ flex: 1 }}>
                                    <Select className="custom-select" mode="multiple" showSearch optionFilterProp="label" value={ids} onChange={setIds} placeholder={`Choose ${pickLabel.toLowerCase()}`} maxTagCount="responsive"
                                        options={(pickFrom || []).map((o) => ({ value: o._id, label: o.hasEmail === false && type !== "employees" ? `${o.name} (no email)` : o.name }))} />
                                </div>
                            )}
                        </div>
                    </>
                )}
                <span className="cmp-hint" style={{ display: "block", marginTop: -8, marginBottom: 14 }}>
                    {previewing ? "Checking who it goes to..." : audience && (mode === "all" || ids.length) ? (
                        <>Goes to {audience.count} {audience.count === 1 ? (type === "employees" ? "person" : type === "customers" ? "customer" : "supplier") : (type === "employees" ? "people" : type)}
                            {audience.withoutEmail?.length ? ` - ${audience.withoutEmail.length} with no email${type === "employees" ? " (they'll see it in the app)" : ` (${audience.withoutEmail.slice(0, 3).join(", ")}${audience.withoutEmail.length > 3 ? "..." : ""}) - copy their links from the results`}` : ""}.</>
                    ) : ""}
                </span>

                {twoWay && (
                    <>
                        <div className="cmp-field">
                            <label>Our side answered by</label>
                            <div className="cmp-choice">
                                <button type="button" className={`cmp-choice-card${ourBy === "role" ? " selected" : ""}`} onClick={() => setOurBy("role")}>
                                    <div className="cmp-choice-card-title">Everyone with a role</div>
                                    <div className="cmp-choice-card-text">Shared between them - whoever answers first takes it.</div>
                                </button>
                                <button type="button" className={`cmp-choice-card${ourBy === "person" ? " selected" : ""}`} onClick={() => setOurBy("person")}>
                                    <div className="cmp-choice-card-title">One person</div>
                                    <div className="cmp-choice-card-text">Goes straight to them.</div>
                                </button>
                            </div>
                        </div>
                        <div className="cmp-field" style={{ marginTop: -6 }}>
                            {ourBy === "role" ? (
                                <>
                                    <Select className="custom-select" value={ourRole} onChange={setOurRole} options={ourOptions?.roles || []} placeholder="Choose a role" />
                                    {ourOptions && (
                                        <span className="cmp-hint">
                                            {ourSuggested.length === 0 ? "Nobody with this role has a WFC360 login - choose another role or one person."
                                                : ourSuggested.length === 1 ? `Goes to ${ourSuggested[0].name}.`
                                                    : `Shared between ${ourSuggested.length}: ${ourSuggested.slice(0, 4).map((p) => p.name).join(", ")}${ourSuggested.length > 4 ? "..." : ""}.`}
                                            {" "}They answer our side for each {type === "suppliers" ? "supplier" : "customer"} on the web. Managers can too.
                                        </span>
                                    )}
                                </>
                            ) : (
                                <Select className="custom-select" showSearch optionFilterProp="label" value={ourId} onChange={setOurId} placeholder="Choose someone with a WFC360 login"
                                    options={(ourOptions?.people || []).map((p) => ({ value: p._id, label: p.name }))} />
                            )}
                        </div>
                    </>
                )}

                {type === "employees" && (
                    <div className="cmp-field" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                        <Switch checked={anonymous} onChange={setAnonymous} />
                        <span><b>Anonymous</b> <span className="cmp-muted">- answers can't be linked to anyone. Results show once 3 people have answered.</span></span>
                    </div>
                )}

                <div style={{ display: "flex", gap: 12 }}>
                    <div className="cmp-field" style={{ flex: 1 }}>
                        <label>Goes out</label>
                        <DatePicker className="generic-input-control" format="DD/MM/YYYY" allowClear={false} value={opensOn} onChange={changeOpensOn} disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))} />
                    </div>
                    <div className="cmp-field" style={{ flex: 1 }}>
                        <label>Closes</label>
                        <DatePicker className="generic-input-control" format="DD/MM/YYYY" allowClear={false} value={closesOn} onChange={setClosesOn} disabledDate={(d) => d && opensOn && !d.isAfter(opensOn, "day")} />
                    </div>
                </div>
                <div className="cmp-field">
                    <label>How often</label>
                    <div className="cmp-choice">
                        {HOW_OFTEN.map((option) => (
                            <button key={option.key} type="button" className={`cmp-choice-card${howOften === option.key ? " selected" : ""}`} onClick={() => setHowOften(option.key)} style={{ padding: "8px 10px" }}>
                                <div className="cmp-choice-card-title">{option.title}</div>
                            </button>
                        ))}
                    </div>
                    {howOften === "custom" && (
                        <InputNumber min={1} max={60} precision={0} size="large" style={{ width: 200 }} value={customMonths} onChange={(v) => setCustomMonths(v || 1)} addonBefore="Every" addonAfter="months" />
                    )}
                    <span className="cmp-hint">{when()}</span>
                </div>
            </div>
        </Modal>
    );
};
